import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ITraining } from '../../types';
import { apiService } from '../../api/api.service';
import { arrayMove } from '@dnd-kit/sortable';
import { isTrainingPublished, createPublishAtString, createPublishAtFromDateTime, extractTimeFromPublishAt, extractDateFromPublishAt } from '../../utils/dateUtils';

// Default publish time (10:00)
const DEFAULT_PUBLISH_TIME = '10:00';


// Initial state interface
interface TrainingsState {
    publishedTrainings: ITraining[];
    scheduledTrainings: ITraining[];
    isLoading: boolean;
    isSaving: boolean;
    globalPublishTime: string;
    error: string | null;
}

// Initial state
const initialState: TrainingsState = {
    publishedTrainings: [],
    scheduledTrainings: [],
    isLoading: false,
    isSaving: false,
    globalPublishTime: DEFAULT_PUBLISH_TIME,
    error: null,
};

// Async thunks
export const fetchTrainings = createAsyncThunk(
    'trainings/fetchTrainings',
    async (language: string, { rejectWithValue }) => {
        try {
            const allTrainings: ITraining[] = await apiService.getVideoList('training');
            return { trainings: allTrainings, language };
        } catch (error) {
            return rejectWithValue('Failed to fetch trainings');
        }
    }
);

export const addTraining = createAsyncThunk(
    'trainings/addTraining',
    async ({ training, language, videoFile, thumbnailFile }: {
        training: Partial<ITraining>,
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { trainings: TrainingsState };

            // Используем время и дату из данных тренировки или значения по умолчанию
            const publishTime = training.time || state.trainings.globalPublishTime;
            const publishDate = training.date || new Date().toISOString().split('T')[0];
            const publishAt = createPublishAtFromDateTime(publishDate, publishTime);

            // Загружаем файлы, если они предоставлены
            let videoFilePath = undefined;
            let previewFilePath = undefined;

            if (videoFile) {
                const videoUploadResponse = await apiService.uploadVideoFile(videoFile);
                videoFilePath = videoUploadResponse.file_path;
            }

            if (thumbnailFile) {
                const previewUploadResponse = await apiService.uploadPreviewFile(thumbnailFile);
                previewFilePath = previewUploadResponse.file_path;
            }

            const trainingWithMetadata = {
                title: training.title,
                description: training.description,
                language: language.toUpperCase(),
                publish_at: publishAt,
                youtube_url: training.youtube_url,
                video_file_path: videoFilePath,
                preview_file_path: previewFilePath,
                preview_time: training.preview_time ? parseFloat(training.preview_time) : undefined,
                generate_preview_from_video: training.generate_preview_from_video,
                order: state.trainings.scheduledTrainings.length + 1
            };

            const response = await apiService.uploadVideo('training', trainingWithMetadata as any);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to add training');
        }
    }
);

export const editTraining = createAsyncThunk(
    'trainings/editTraining',
    async ({
        id,
        training,
        language,
        videoFile,
        thumbnailFile
    }: {
        id: number,
        training: Partial<ITraining>,
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { trainings: TrainingsState };

            // Используем время и дату из данных тренировки или значения по умолчанию
            const publishTime = training.time || state.trainings.globalPublishTime;
            const publishDate = training.date || new Date().toISOString().split('T')[0];
            const publishAt = createPublishAtFromDateTime(publishDate, publishTime);

            // Загружаем файлы, если они предоставлены
            let videoFilePath = undefined;
            let previewFilePath = undefined;

            if (videoFile) {
                const videoUploadResponse = await apiService.uploadVideoFile(videoFile);
                videoFilePath = videoUploadResponse.file_path;
            }

            if (thumbnailFile) {
                const previewUploadResponse = await apiService.uploadPreviewFile(thumbnailFile);
                previewFilePath = previewUploadResponse.file_path;
            }

            // Подготавливаем данные для отправки
            const trainingWithMetadata: any = {
                title: training.title,
                description: training.description,
                language: language.toUpperCase(),
                publish_at: publishAt,
                youtube_url: training.youtube_url,
                video_file_path: videoFilePath,
                preview_file_path: previewFilePath,
                preview_time: training.preview_time ? parseFloat(training.preview_time) : undefined,
                generate_preview_from_video: training.generate_preview_from_video
            };

            // Удаляем неопределенные значения
            Object.keys(trainingWithMetadata).forEach(key => {
                if (trainingWithMetadata[key] === undefined) {
                    delete trainingWithMetadata[key];
                }
            });

            const response = await apiService.editVideo('training', id, trainingWithMetadata);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to edit training');
        }
    }
);

export const deleteTraining = createAsyncThunk(
    'trainings/deleteTraining',
    async (id: number, { rejectWithValue }) => {
        try {
            await apiService.deleteVideo('training', id);
            return id;
        } catch (error) {
            return rejectWithValue('Failed to delete training');
        }
    }
);

export const saveTrainingsOrder = createAsyncThunk(
    'trainings/saveTrainingsOrder',
    async (language: string, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { trainings: TrainingsState };
            const { scheduledTrainings, globalPublishTime } = state.trainings;

            // Format data according to API requirements: { videos: [{ id, order, time }] }
            const videos = scheduledTrainings
                .filter(training => training.language === language.toUpperCase())
                .map(training => ({
                    id: training.id,
                    order: training.order,
                    time: training.publish_at ? extractTimeFromPublishAt(training.publish_at) : (globalPublishTime || DEFAULT_PUBLISH_TIME)
                }));

            if (videos.length > 0) {
                await apiService.saveVideosOrder(videos);
            }

            return { language, globalPublishTime: globalPublishTime || DEFAULT_PUBLISH_TIME };
        } catch (error) {
            return rejectWithValue('Failed to save trainings order');
        }
    }
);

// Slice
const trainingsSlice = createSlice({
    name: 'trainings',
    initialState,
    reducers: {
        setGlobalPublishTime: (state, action: PayloadAction<string>) => {
            state.globalPublishTime = action.payload;
        },
        reorderScheduledTrainings: (state, action: PayloadAction<{ activeId: string; overId: string }>) => {
            const { activeId, overId } = action.payload;
            const items = [...state.scheduledTrainings];
            const activeIndex = items.findIndex(item => item.id.toString() === activeId);
            const overIndex = items.findIndex(item => item.id.toString() === overId);

            if (activeIndex !== -1 && overIndex !== -1) {
                const newItems = arrayMove(items, activeIndex, overIndex);
                // Обновляем порядок для всех элементов
                state.scheduledTrainings = newItems.map((item, index) => ({
                    ...item,
                    order: index + 1
                }));
            }
        },
        updateTrainingFromStatus: (state, action: PayloadAction<{
            trainingId: number;
            updates: Partial<ITraining>
        }>) => {
            const { trainingId, updates } = action.payload;

            // Обновляем тренировку в опубликованных
            const publishedIndex = state.publishedTrainings.findIndex(t => t.id === trainingId);
            if (publishedIndex !== -1) {
                state.publishedTrainings[publishedIndex] = {
                    ...state.publishedTrainings[publishedIndex],
                    ...updates
                };
                return;
            }

            // Обновляем тренировку в запланированных
            const scheduledIndex = state.scheduledTrainings.findIndex(t => t.id === trainingId);
            if (scheduledIndex !== -1) {
                state.scheduledTrainings[scheduledIndex] = {
                    ...state.scheduledTrainings[scheduledIndex],
                    ...updates
                };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch trainings
            .addCase(fetchTrainings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTrainings.fulfilled, (state, action) => {
                state.isLoading = false;

                const { trainings } = action.payload;

                // Sort by order property
                const sortedTrainings = [...trainings]
                    .map(training => ({
                        ...training,
                        order: training.order || 1
                    }))
                    .sort((a, b) => a.order - b.order);

                // Split into published and scheduled based on publish_at time
                // Note: is_scheduled field from the API is ignored - status is determined by publish_at
                const published = sortedTrainings
                    .filter(t => isTrainingPublished(t.publish_at));

                const scheduled = sortedTrainings
                    .filter(t => !isTrainingPublished(t.publish_at));

                state.publishedTrainings = published;
                state.scheduledTrainings = scheduled;
            })
            .addCase(fetchTrainings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // Add training
            .addCase(addTraining.fulfilled, (state, action) => {
                const newTraining = action.payload;

                // Добавляем тренировку в правильный массив
                if (isTrainingPublished(newTraining.publish_at)) {
                    state.publishedTrainings.push(newTraining);
                } else {
                    // Для запланированных тренировок добавляем в конец
                    const newOrder = state.scheduledTrainings.length + 1;
                    state.scheduledTrainings.push({
                        ...newTraining,
                        order: newOrder
                    });
                    // Сортируем по order после добавления
                    state.scheduledTrainings.sort((a, b) => (a.order || 0) - (b.order || 0));
                }
            })

            // Edit training
            .addCase(editTraining.fulfilled, (state, action) => {
                const updatedTraining = action.payload;

                // Удаляем тренировку из обоих массивов
                state.publishedTrainings = state.publishedTrainings.filter(t => t.id !== updatedTraining.id);
                state.scheduledTrainings = state.scheduledTrainings.filter(t => t.id !== updatedTraining.id);

                // Добавляем тренировку в правильный массив
                if (isTrainingPublished(updatedTraining.publish_at)) {
                    state.publishedTrainings.push(updatedTraining);
                } else {
                    // Для запланированных тренировок сохраняем порядок
                    const existingOrder = state.scheduledTrainings.find(t => t.id === updatedTraining.id)?.order || 1;
                    state.scheduledTrainings.push({
                        ...updatedTraining,
                        order: existingOrder
                    });
                    // Сортируем по order после редактирования
                    state.scheduledTrainings.sort((a, b) => (a.order || 0) - (b.order || 0));
                }
            })

            // Delete training
            .addCase(deleteTraining.fulfilled, (state, action) => {
                const deletedId = action.payload;
                // Check if training is in published list
                if (state.publishedTrainings.some(t => t.id === deletedId)) {
                    state.publishedTrainings = state.publishedTrainings.filter(t => t.id !== deletedId);
                } else {
                    state.scheduledTrainings = state.scheduledTrainings.filter(t => t.id !== deletedId);
                    // Пересчитываем порядок после удаления
                    state.scheduledTrainings.forEach((training, index) => {
                        training.order = index + 1;
                    });
                }
            })

            // Save trainings order
            .addCase(saveTrainingsOrder.pending, (state) => {
                state.isSaving = true;
            })
            .addCase(saveTrainingsOrder.fulfilled, (state, action) => {
                state.isSaving = false;
                const { language, globalPublishTime } = action.payload;

                // Update publish time for scheduled trainings of the current language
                state.scheduledTrainings = state.scheduledTrainings.map(training => {
                    if (training.language === language.toUpperCase()) {
                        // Сохраняем существующую дату, обновляем только время
                        const existingDate = extractDateFromPublishAt(training.publish_at);
                        const publishAt = createPublishAtFromDateTime(existingDate, globalPublishTime);
                        return {
                            ...training,
                            publish_at: publishAt,
                            is_scheduled: !isTrainingPublished(publishAt)
                        };
                    }
                    return training;
                });
            })
            .addCase(saveTrainingsOrder.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload as string;
            });
    }
});

export const { setGlobalPublishTime, reorderScheduledTrainings, updateTrainingFromStatus } = trainingsSlice.actions;

export default trainingsSlice.reducer; 